import React, { useEffect, useState } from 'react';
import { Settings, Info } from 'lucide-react';
import { mockAgentConfigs } from '../../data/mockData';
import { useAgent } from '../../contexts/AgentContext';
import { AppCard } from '../base/AppCard';
import { AppInput } from '../base/AppInput';
interface AgentParametriTabProps {
  onDataChange?: () => void;
}
export function AgentParametriTab({
  onDataChange
}: AgentParametriTabProps) {
  const {
    activeAgentId
  } = useAgent();
  const agentConfig = mockAgentConfigs[activeAgentId] || mockAgentConfigs['default'];
  const [modelName, setModelName] = useState(agentConfig.modelName);
  const [temperatureLevel, setTemperatureLevel] = useState(agentConfig.temperatureLevel);
  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    const config = mockAgentConfigs[activeAgentId] || mockAgentConfigs['default'];
    setModelName(config.modelName);
    setTemperatureLevel(config.temperatureLevel);
  }, [activeAgentId]);
  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModelName(e.target.value);
    onDataChange?.();
  };
  const handleTemperatureChange = (value: number) => {
    setTemperatureLevel(value);
    onDataChange?.();
  };
  const temperatureLevels = [{
    value: 1,
    label: 'Molto preciso',
    description: 'Risposte deterministiche e coerenti'
  }, {
    value: 2,
    label: 'Preciso',
    description: 'Risposte affidabili con poca variazione'
  }, {
    value: 3,
    label: 'Bilanciato',
    description: 'Equilibrio tra precisione e creatività'
  }, {
    value: 4,
    label: 'Creativo',
    description: 'Risposte più varie e originali'
  }, {
    value: 5,
    label: 'Molto creativo',
    description: 'Massima varietà e imprevedibilità'
  }];
  const availableModels = ['gpt-4-turbo', 'gpt-4', 'gpt-4.1-mini', 'gpt-3.5-turbo', 'claude-3-opus', 'claude-3-sonnet'];
  return <div className="space-y-6" data-id="element-748">
      {/* Header */}
      <AppCard className="bg-gradient-to-r from-blue-50 to-blue-50/30 dark:from-blue-900/20 dark:to-blue-900/10" data-id="element-749">
        <div className="flex items-start" data-id="element-750">
          <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl mr-3.5" data-id="element-751">
            <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-752" />
          </div>
          <div data-id="element-753">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-754">
              Parametri dell'agente
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-755">
              Configura il modello e il comportamento di base dell'agente AI.
            </p>
          </div>
        </div>
      </AppCard>

      {/* Configuration Form */}
      <AppCard data-id="element-756">
        <div className="space-y-6" data-id="element-757">
          {/* Model Selection */}
          <div data-id="element-758">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-759">
              Modello
            </label>
            <select value={modelName} onChange={handleModelChange} className="w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-900 dark:text-gray-100 py-2.5 px-3.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all duration-180" data-id="element-760">
              {availableModels.map(model => <option key={model} value={model} data-id="element-761">
                  {model}
                </option>)}
            </select>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400" data-id="element-762">
              Seleziona il modello di linguaggio da utilizzare per questo agente
            </p>
          </div>

          {/* Role (Read-only) */}
          <div data-id="element-763">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-764">
              Ruolo
            </label>
            <div className="w-full bg-gray-50 dark:bg-dark-subtle border border-gray-200 dark:border-dark-subtle text-gray-600 dark:text-gray-400 py-2.5 px-3.5 rounded-xl text-sm" data-id="element-765">
              {agentConfig.role}
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400" data-id="element-766">
              Il ruolo dell'agente è definito dalla sua configurazione di base
            </p>
          </div>

          {/* Temperature Slider */}
          <div data-id="element-767">
            <div className="flex items-center justify-between mb-3" data-id="element-768">
              <div className="flex items-center gap-2" data-id="element-769">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-770">
                  Precisione / Creatività
                </label>
                <div className="relative" data-id="element-771">
                  <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" data-id="element-772">
                    <Info className="w-4 h-4" data-id="element-773" />
                  </button>
                  {showTooltip && <div className="absolute left-6 top-0 w-72 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg p-3 shadow-xl z-10" data-id="element-774">
                      <p className="font-medium mb-2" data-id="element-775">Parametro Temperatura</p>
                      <p className="mb-2" data-id="element-776">
                        Controlla la casualità delle risposte dell'agente.
                        Valori più bassi producono risposte più prevedibili e
                        coerenti, mentre valori più alti generano risposte più
                        creative e varie.
                      </p>
                      <div className="space-y-1 text-[11px]" data-id="element-777">
                        <p data-id="element-778">
                          <strong data-id="element-779">Livello 1-2:</strong> Ideale per task che
                          richiedono precisione (analisi dati, calcoli)
                        </p>
                        <p data-id="element-780">
                          <strong data-id="element-781">Livello 3:</strong> Bilanciato per uso
                          generale
                        </p>
                        <p data-id="element-782">
                          <strong data-id="element-783">Livello 4-5:</strong> Migliore per contenuti
                          creativi (scrittura, brainstorming)
                        </p>
                      </div>
                      <div className="absolute -left-1 top-2 w-2 h-2 bg-gray-900 dark:bg-gray-800 transform rotate-45" data-id="element-784"></div>
                    </div>}
                </div>
              </div>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400" data-id="element-785">
                {temperatureLevels[temperatureLevel - 1].label}
              </span>
            </div>

            {/* Slider */}
            <div className="relative" data-id="element-786">
              <input type="range" min="1" max="5" step="1" value={temperatureLevel} onChange={e => handleTemperatureChange(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-dark-subtle rounded-lg appearance-none cursor-pointer slider" style={{
              background: `linear-gradient(to right, #91c429 0%, #91c429 ${(temperatureLevel - 1) / 4 * 100}%, #e5e7eb ${(temperatureLevel - 1) / 4 * 100}%, #e5e7eb 100%)`
            }} data-id="element-787" />
              <div className="flex justify-between mt-2 px-1" data-id="element-788">
                {[1, 2, 3, 4, 5].map(level => <button key={level} onClick={() => handleTemperatureChange(level)} className={`w-2 h-2 rounded-full transition-all ${temperatureLevel >= level ? 'bg-blue-600 dark:bg-blue-400' : 'bg-gray-300 dark:bg-dark-medium'}`} data-id="element-789" />)}
              </div>
            </div>

            {/* Level Labels */}
            <div className="flex justify-between mt-3 text-xs text-gray-500 dark:text-gray-400" data-id="element-790">
              <span data-id="element-791">Precisione</span>
              <span data-id="element-792">Creatività</span>
            </div>

            {/* Description */}
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg" data-id="element-793">
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-794">
                {temperatureLevels[temperatureLevel - 1].description}
              </p>
            </div>
          </div>
        </div>
      </AppCard>
    </div>;
}